//@ts-strict-ignore
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { DocumentNode, useQuery } from '@apollo/client';
import Dropdown from '../connectedForm/Dropdown';

import * as QueryHelper from '../../utils/QueryHelper';
import getIn from '../connectedForm/utils/getIn';
import componentQuery from '../../utils/componentQuery';
import { Direction, FilterOperation, IFilterInput, Maybe } from '../../graphql/schema/p4types';
import { IDropdownProps } from '../../@types/dropdown';
import { mergeWith } from 'lodash';
import { mergeCustomizerArrayConcat } from '../../graphql/hooks/useWorkspace';

type GetVariablesType = (config: any, genericFilterString?: string | null) => any;

type IQueryDropdownProps = {
  preLoad?: boolean;
  customFilter?: (data: any) => Array<IFilterInput>;
  dropdown?: any;
  column?: any;
  query?: DocumentNode;
  scanQuery?: DocumentNode;
  objects?: Array<any>;
  codes?: Array<any>;
  type?: string;
  onChange?: Function;
  rowData?: any;
  additionalVariables?: any;
  selectModal?: Maybe<any>;
};

/**
 * Wrapper class for dropdown to handle data querying.
 */
const QueryDropdown = forwardRef((props: IQueryDropdownProps & IDropdownProps, ref) => {
  const [genericFilter, setGenericFilter] = useState<string | null | undefined>(null);
  const [value, setValue] = useState<any>(props.value);
  const [processedData, setProcessedData] = useState<Array<any>>([]);
  const { predefinedFilters, remoteFilter, preLoad } = props;

  /**
   * Compile the various sources for query variables into one place.
   * @param config - customVariables (Function that takes props)
   * @param genericFilterString - Generic filter field.
   * @returns - Array of graphql query variables
   */
  const getVariables: GetVariablesType = useCallback(
    (config, genericFilterString) => {
      if (genericFilterString) {
        setGenericFilter(genericFilterString);
      }

      const filters: IFilterInput[] = [];
      if (predefinedFilters) {
        filters.push(...predefinedFilters);
      }
      if (genericFilterString) {
        filters.push({
          filterOperation: FilterOperation.Contains,
          filterParameters: {
            field: 'generic',
            value: genericFilterString || genericFilter,
          },
        });
      }

      const hasGenericFilter = genericFilterString && genericFilterString.length > 1;
      const fetchFirst = remoteFilter && !hasGenericFilter && !preLoad ? 0 : 25;

      let variables = {
        first: fetchFirst,
        after: 0,
        orderBy: 'id',
        orderDirection: Direction.Asc,
        filters: filters,
      };
      let customVariables = {};
      if (config.customVariables) {
        customVariables = config.customVariables(props);
      }
      variables = { ...variables, ...customVariables };

      //@props.customFilter (Function that takes props.rowData and returns a filter or array of filters.)
      if (props.customFilter) {
        variables.filters = variables.filters.concat(props.customFilter(props.rowData));
      }

      if (props.additionalVariables) {
        variables = mergeWith(variables, props.additionalVariables, mergeCustomizerArrayConcat);
      }

      return variables;
    },
    [genericFilter, preLoad, predefinedFilters, props, remoteFilter]
  );

  const getScanVariables = (genericFilterString) => {
    return {
      filter: {
        filterOperation: 'CONTAINS',
        filterParameters: {
          field: 'generic',
          value: genericFilterString,
        },
      },
    };
  };

  const dropdownProps = useMemo(() => {
    const fromProps = props.dropdown ?? {};
    if (props?.column?.options) {
      const dropdown = props.column.options.dropdown;
      return { ...dropdown, ...fromProps };
    }
    return fromProps;
  }, [props?.column?.options, props.dropdown]);

  const query = dropdownProps.query ?? props.query;
  const scanQuery = dropdownProps.scanQuery ?? props.scanQuery;

  const { data, previousData, refetch, fetchMore } = useQuery(query, {
    variables: getVariables({ ...props, ...dropdownProps }),
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!value) {
        // Filter objects - reset value to include full object data rather than just the id
        // Only needs to do this when the value is not already the full object, i.e. on initial load
        const value = props.value;
        if (props.type === 'filterObjects' && value && props.codes && data?.list?.edges) {
          if (!getIn(value[0], props.labelKey)) {
            setValue(
              data.list.edges
                .filter((item) => props.objects?.find((obj) => obj.id === item.node.id))
                .map((val) => val.node)
            );
          }
        }
      }

      let processed = QueryHelper.processData(data);

      if (dropdownProps.normalizeData && dropdownProps.normalizeData instanceof Function) {
        processed = dropdownProps.normalizeData(processed);
      }

      setProcessedData(processed);
    },
  });

  const latestData = useMemo(() => data ?? previousData, [data, previousData]);

  const onMenuScrollToBottom = useCallback(() => {
    const previousPageInfo = latestData?.[Object.keys(latestData)[0]]?.pageInfo;
    if (!previousPageInfo?.hasNextPage) {
      return;
    }

    return fetchMore({
      variables: {
        ...getVariables({ ...props, ...dropdownProps }, genericFilter),
        after: previousPageInfo.endCursor,
      },
    });
  }, [dropdownProps, fetchMore, getVariables, latestData, props]);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  return (
    <Dropdown
      ref={ref}
      {...dropdownProps}
      {...props}
      cache={false}
      value={value}
      data={processedData}
      component={Dropdown}
      refreshQuery={(input) => {
        return refetch(getVariables({ ...props, ...dropdownProps }, input));
      }}
      scanQuery={(input, keyCode) => {
        if (!scanQuery) return null;
        componentQuery(scanQuery, getScanVariables(input), true).then(({ response }) => {
          if (response && response.data) {
            props.onChange?.(response.data, keyCode);
          }
        });
      }}
      remoteFilter={true}
      onMenuScrollToBottom={onMenuScrollToBottom}
    />
  );
});

export default QueryDropdown;
